import { FC, lazy } from 'react';
import { useParams } from 'react-router-dom';
import { useBalancingCodes } from 'src/apis/monolith/use-balancing-codes';
import {
  TradeProposalRequest,
  useProposal,
} from 'src/apis/monolith/use-proposal';
import { useSecondaryConfig } from 'src/apis/monolith/use-secondary-config';
import { useTraderLists } from 'src/apis/monolith/use-trader-lists';
import { useTsoSecondarySettings } from 'src/apis/monolith/use-tso-secondary-settings';
import { PageSpinner } from 'src/components/spinner-container';
import { Successful } from 'src/hooks/use-axios';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { useTitle } from 'src/hooks/use-title';
import { useRemitReportingSubscriptionInfo } from 'src/pages/reporting/secondary-tradings/overview/use-remit-reporting-subscription-info';

const Page = lazy(() => import('./page'));

export const ReportingSecondaryTradeProposal: FC = () => {
  const { proposalId } = useParams<{ proposalId: string }>();
  useTitle(`Proposal ID ${proposalId}`);
  const monolithUser = useOptionalAuthenticatedMonolithUser();

  const tradeProposal = useProposal({ proposalId });

  if (!tradeProposal.response) return <PageSpinner />;

  if (monolithUser?.isShipper) {
    return <LoadShipperResources tradeProposal={tradeProposal} />;
  } else {
    return <Page tradeProposal={tradeProposal} />;
  }
};

const LoadShipperResources: FC<{
  tradeProposal: Successful<TradeProposalRequest>;
}> = ({ tradeProposal }) => {
  const proposal = tradeProposal.response.data;

  const secondaryConfig = useSecondaryConfig({
    networkPointId: proposal.networkPoint.id,
  });
  const balancingCodes = useBalancingCodes({ proposal });
  const traderLists = useTraderLists();
  const remitReportingSubscriptionInfo =
    useRemitReportingSubscriptionInfo('SECONDARY');

  const tso0SecondarySettings = useTsoSecondarySettings({
    tsoId: proposal.networkPoint.tso0Id,
  });
  const tso1SecondarySettings = useTsoSecondarySettings({
    tsoId: proposal.networkPoint.tso1Id,
  });

  if (
    !secondaryConfig.response ||
    !remitReportingSubscriptionInfo.response ||
    !balancingCodes.response ||
    !traderLists.response ||
    (tso0SecondarySettings && !tso0SecondarySettings.loaded) ||
    (tso1SecondarySettings && !tso1SecondarySettings.loaded)
  )
    return <PageSpinner />;

  return (
    <Page
      tradeProposal={tradeProposal}
      secondaryConfig={secondaryConfig.response.data}
      balancingCodes={balancingCodes.response.data}
      traderLists={traderLists.response.data}
      remitReportingSubscriptionInfo={
        remitReportingSubscriptionInfo.response.data
      }
      tso0SecondarySettings={tso0SecondarySettings?.response?.data}
      tso1SecondarySettings={tso1SecondarySettings?.response?.data}
    />
  );
};
